import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowClockwise, CheckCircle, CloudSun, Eye, Plus, Trash, X } from "@phosphor-icons/react";
import { aender, api, lies, schick, weg } from "../api/client";
import type { PersonalZeile, Tag } from "../api/types";
import { Blur, Dialog, Meldung } from "../components/Bausteine";
import { Diktat } from "../components/Diktat";
import { FotoLeiste } from "../components/FotoLeiste";
import { datum } from "../lib/format";
import { usePfad } from "../lib/pfad";
import { useBeteiligte } from "./projekt/BeteiligteReiter";
import { Standort, WOCHENTAG } from "./projekt/TagebuchReiter";
import { useProjekt } from "./projekt/ProjektSeite";

const WOCHENTAG_LANG = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

/** Ein Tag im Bautagebuch (SPEC §14). */
export function TagSeite() {
  const { tagId = "" } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const hinweis = (useLocation().state as { hinweis?: string } | null)?.hinweis;
  const schluessel = ["tag", tagId];
  const t = useQuery({ queryKey: schluessel, queryFn: () => lies<Tag>(`/tagebuch/${tagId}`) });
  const projekt = useProjekt(t.data?.projekt_id).data;
  usePfad(projekt && t.data ? [
    { titel: projekt.name, nach: `/p/${projekt.kurz}/besprechungen` },
    { titel: "Bautagebuch", nach: `/p/${projekt.kurz}/tagebuch` },
    { titel: `${WOCHENTAG[new Date(`${t.data.datum}T12:00:00`).getDay()]} ${datum(t.data.datum)}` },
  ] : []);
  const [abschluss, setAbschluss] = useState(false);
  const [loeschen, setLoeschen] = useState(false);

  const fertig = (neu: Tag) => {
    qc.setQueryData(schluessel, { ...t.data, ...neu });
    qc.invalidateQueries({ queryKey: ["tagebuch", neu.projekt_id] });
  };
  const aendern = useMutation({
    mutationFn: (r: Partial<Tag>) => aender<Tag>(`/tagebuch/${tagId}`, r),
    onSuccess: fertig,
  });
  const wetter = useMutation({ mutationFn: () => schick<Tag>(`/tagebuch/${tagId}/wetter`), onSuccess: fertig });
  const abschliessen = useMutation({
    mutationFn: () => schick<Tag>(`/tagebuch/${tagId}/abschluss`),
    onSuccess: (neu) => { fertig(neu); setAbschluss(false); },
  });
  const entfernen = useMutation({
    mutationFn: () => weg(`/tagebuch/${tagId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tagebuch", t.data?.projekt_id] });
      nav(`/p/${projekt?.kurz ?? t.data?.projekt_id}/tagebuch`, { replace: true });
    },
  });

  if (t.error) return <main className="seite"><Meldung fehler={t.error} /></main>;
  if (!t.data) return null;
  const d = t.data;
  const offen = d.status === "entwurf" && d.meine_rolle !== "lesen";
  const feld = (k: "geraete" | "leistungen" | "vorkommnisse" | "anweisungen" | "besuche", titel: string, platz: string) => (
    <div className="feld breit">
      <div className="feld-kopf"><span>{titel}</span>
        {offen && <Diktat text={d[k]} speichern={(v) => aendern.mutate({ [k]: v })} />}</div>
      <Blur mehrzeilig disabled={!offen} wert={d[k]} placeholder={platz} aria-label={titel}
            speichern={(v) => aendern.mutate({ [k]: v })} />
    </div>
  );

  return (
    <main className="seite">
      <div className="seite-kopf">
        <div>
          <div className="mute">Bautagebuch</div>
          <h1>{WOCHENTAG_LANG[new Date(`${d.datum}T12:00:00`).getDay()]}, {datum(d.datum)}</h1>
          <div className="dim">{d.projekt?.name}{d.verfasst_name && ` · geführt von ${d.verfasst_name}`}</div>
        </div>
        <div className="luft" />
        {d.status === "entwurf" ? <span className="chip warn">offen</span> : <span className="chip gut">abgeschlossen</span>}
      </div>

      {hinweis && !d.wetter && <div className="hinweise"><div className="hinweis">{hinweis}</div></div>}
      {projekt && !projekt.lat && offen && <Standort projekt={projekt} />}

      <section className="karte">
        <div className="karte-kopf">
          <h2>Wetter</h2>
          <div className="luft" />
          {offen && projekt?.lat != null && (
            <button className="knopf leise" disabled={wetter.isPending} onClick={() => wetter.mutate()}>
              <ArrowClockwise size={16} /> {d.wetter ? "Neu abrufen" : "Abrufen"}
            </button>
          )}
        </div>
        {d.wetter ? (
          <div className="wetter">
            <CloudSun size={34} weight="light" />
            <div className="wetter-werte">
              <div><b>{d.wetter.temp_min.toFixed(0)}–{d.wetter.temp_max.toFixed(0)} °C</b>{d.wetter.bedingung && `, ${d.wetter.bedingung}`}</div>
              <div className="mute">{zahl(d.wetter.niederschlag_mm)} mm Niederschlag · Wind bis {d.wetter.wind_max_kmh.toFixed(0)} km/h · {zahl(d.wetter.sonne_h)} h Sonne</div>
              <div className="mute klein">{d.wetter.quelle}, abgerufen {new Date(d.wetter.abgerufen_am).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</div>
            </div>
          </div>
        ) : <div className="leer" style={{ padding: "4px 0 12px" }}>Kein Wetter abgerufen.</div>}
        <Meldung fehler={wetter.error} />
        <div className="raster" style={{ marginTop: 10 }}>
          <label className="feld"><span>Wetter (eigene Beobachtung)</span>
            <Blur disabled={!offen} wert={d.wetter_text} placeholder="z. B. Bodenfrost bis 9 Uhr"
                  speichern={(v) => aendern.mutate({ wetter_text: v })} /></label>
          <label className="feld"><span>Arbeitszeit</span>
            <Blur disabled={!offen} wert={d.arbeitszeit} placeholder="7–16 Uhr" speichern={(v) => aendern.mutate({ arbeitszeit: v })} /></label>
        </div>
      </section>

      <section className="karte">
        <div className="karte-kopf"><h2>Personal</h2>
          {d.personal_summe > 0 && <span className="mute">{d.personal_summe} Personen</span>}</div>
        <PersonalTabelle tag={d} offen={offen} gespeichert={fertig} />
      </section>

      <section className="karte">
        <div className="raster">
          {feld("leistungen", "Ausgeführte Leistungen", "Was wurde wo gebaut?")}
          {feld("geraete", "Geräte", "z. B. Minibagger 2,5 t, Rüttelplatte")}
          {feld("vorkommnisse", "Vorkommnisse, Behinderungen", "Unterbrechungen, Schäden, Unfälle, fehlende Pläne …")}
          {feld("anweisungen", "Anweisungen an Firmen", "Was wurde wem aufgegeben?")}
          {feld("besuche", "Besuche", "Bauherr, Behörden, Planer")}
        </div>
        <Meldung fehler={aendern.error} />
        <FotoLeiste projektId={d.projekt_id} ziel="tagebuch_id" zielId={d.id} kann={offen} />
      </section>

      <div className="leiste-unten">
        <a className="knopf" href={`/api/projekte/${d.projekt_id}/tagebuch/druck?von=${d.datum}&bis=${d.datum}`}
           target="_blank" rel="noreferrer"><Eye size={16} /> Druckansicht</a>
        {offen && <button className="knopf leise" onClick={() => setLoeschen(true)}><Trash size={16} /> Löschen</button>}
        <div className="luft" />
        {offen && <button className="knopf haupt" onClick={() => setAbschluss(true)}><CheckCircle size={16} /> Tag abschließen</button>}
      </div>

      <Dialog offen={abschluss} zu={() => setAbschluss(false)} titel="Tag abschließen" fuss={<>
        <button className="knopf leise" onClick={() => setAbschluss(false)}>Abbrechen</button>
        <button className="knopf haupt" disabled={abschliessen.isPending} onClick={() => abschliessen.mutate()}>Abschließen</button>
      </>}>
        <p style={{ marginTop: 0 }}>Danach ist der Tag unveränderlich, auch seine Fotos. Lesende sehen ihn ab dann.</p>
        <Meldung fehler={abschliessen.error} />
      </Dialog>
      <Dialog offen={loeschen} zu={() => setLoeschen(false)} titel="Tag löschen" fuss={<>
        <button className="knopf leise" onClick={() => setLoeschen(false)}>Abbrechen</button>
        <button className="knopf haupt" disabled={entfernen.isPending} onClick={() => entfernen.mutate()}>Löschen</button>
      </>}>
        <p style={{ marginTop: 0 }}>Der Tag {datum(d.datum)} mit allen Angaben und Fotos wird gelöscht.</p>
        <Meldung fehler={entfernen.error} />
      </Dialog>
    </main>
  );
}

function zahl(x: number): string {
  return x.toLocaleString("de-DE", { maximumFractionDigits: 1 });
}

/** Die ganze Liste wird beim Verlassen eines Felds gespeichert (PUT), wenn sich etwas
 *  geaendert hat — wie die Blur-Felder, nur fuer eine Tabelle. */
function PersonalTabelle({ tag, offen, gespeichert }: { tag: Tag; offen: boolean; gespeichert: (t: Tag) => void }) {
  const beteiligte = useBeteiligte(tag.projekt_id).data ?? [];
  const [zeilen, setZeilen] = useState<PersonalZeile[]>(tag.personal);
  useEffect(() => setZeilen(tag.personal), [tag.personal]);
  const speichern = useMutation({
    mutationFn: (personal: PersonalZeile[]) => api<Tag>("PUT", `/tagebuch/${tag.id}/personal`, { personal }),
    onSuccess: gespeichert,
  });
  const sichern = (neu = zeilen) => {
    const sauber = neu.filter((z) => z.firma.trim());
    if (JSON.stringify(sauber) !== JSON.stringify(tag.personal)) speichern.mutate(sauber);
  };
  const setze = (i: number, teil: Partial<PersonalZeile>) =>
    setZeilen(zeilen.map((z, j) => (j === i ? { ...z, ...teil } : z)));
  const firmen = beteiligte.filter((b) => b.art === "auftragnehmer" || b.art === "sonstige");

  return (
    <div className="personal">
      {zeilen.map((z, i) => (
        <div key={i} className="personal-zeile">
          <input className="eingabe anzahl" type="number" min={0} max={999} inputMode="numeric" disabled={!offen}
                 aria-label="Anzahl" value={z.anzahl}
                 onChange={(e) => setze(i, { anzahl: Math.max(0, Math.min(999, Number(e.target.value) || 0)) })}
                 onBlur={() => sichern()} />
          <input className="eingabe" list="personal-firmen" disabled={!offen} aria-label="Firma" placeholder="Firma" value={z.firma}
                 onChange={(e) => {
                   const b = beteiligte.find((x) => x.firma === e.target.value);
                   setze(i, { firma: e.target.value, beteiligter_id: b?.id ?? null });
                 }}
                 onBlur={() => sichern()} />
          <input className="eingabe" disabled={!offen} aria-label="Tätigkeit" placeholder="Tätigkeit" value={z.taetigkeit}
                 onChange={(e) => setze(i, { taetigkeit: e.target.value })} onBlur={() => sichern()} />
          {offen && (
            <button className="knopf leise symbol" aria-label="Zeile entfernen"
                    onClick={() => { const neu = zeilen.filter((_, j) => j !== i); setZeilen(neu); sichern(neu); }}>
              <X size={16} />
            </button>
          )}
        </div>
      ))}
      {zeilen.length === 0 && !offen && <div className="leer">Kein Personal eingetragen.</div>}
      <datalist id="personal-firmen">
        {firmen.map((b) => <option key={b.id} value={b.firma} />)}
      </datalist>
      {offen && (
        <button className="knopf leise" onClick={() => setZeilen([...zeilen, { beteiligter_id: null, firma: "", anzahl: 1, taetigkeit: "" }])}>
          <Plus size={16} /> Firma
        </button>
      )}
      <Meldung fehler={speichern.error} />
    </div>
  );
}
